/**
 * Admin API — frontend-only auth cu PIN din .env.
 *
 * Flow:
 *  1. User intra pe /admin → introduce PIN
 *  2. Comparam PIN-ul cu VITE_ADMIN_PIN sau VITE_SUPER_ADMIN_PIN
 *  3. Daca match, facem UPDATE pe visitors setand role='admin' (sau 'super_admin')
 *     pentru device_id-ul curent
 *  4. Restul paginii foloseste useAdminAuth() care citeste visitors.role
 *
 * NOTA DE SECURITATE:
 *  PIN-ul e in .env (expus in bundle-ul frontend-ului), deci oricine poate
 *  citi sursa JS si vedea PIN-ul. Asta e OK pentru evenimente private (nunti)
 *  unde URL-ul paginii e deja secret. Pentru productie multi-tenant cu multe
 *  evenimente, muta auth-ul intr-un Cloudflare Worker cu hashing + rate-limit.
 */

import { supabase } from './supabase';
import { getDeviceId } from './device';
import type { PhotoRow } from '@/types';

export type AdminRole = 'admin' | 'super_admin';

const ADMIN_PIN = (import.meta.env.VITE_ADMIN_PIN as string | undefined) ?? '';
const SUPER_ADMIN_PIN = (import.meta.env.VITE_SUPER_ADMIN_PIN as string | undefined) ?? '';

/**
 * Verifica un PIN introdus de user.
 * Returneaza 'admin', 'super_admin' sau null daca PIN-ul e gresit.
 */
export function checkPin(input: string): AdminRole | null {
  const pin = input.trim();
  if (!pin) return null;
  if (SUPER_ADMIN_PIN && pin === SUPER_ADMIN_PIN) return 'super_admin';
  if (ADMIN_PIN && pin === ADMIN_PIN) return 'admin';
  return null;
}

/**
 * Seteaza role-ul device-ului curent in visitors.
 * Trebuie sa existe deja un rand in visitors (useVisitor il creeaza la prima vizita).
 */
export async function setMyRole(role: AdminRole): Promise<void> {
  const deviceId = getDeviceId();
  if (!deviceId) throw new Error('No device id');

  const { error } = await supabase
    .from('visitors')
    .update({ role })
    .eq('device_id', deviceId);

  if (error) throw error;
}

/**
 * Citeste role-ul device-ului curent.
 * Returneaza 'admin', 'super_admin' sau null.
 */
export async function getMyRole(): Promise<AdminRole | null> {
  const deviceId = getDeviceId();
  if (!deviceId) return null;

  const { data, error } = await supabase
    .from('visitors')
    .select('role')
    .eq('device_id', deviceId)
    .maybeSingle();

  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[getMyRole]', error.message);
    return null;
  }
  return (data?.role as AdminRole | null) ?? null;
}

/**
 * Sterge role-ul device-ului curent (delogare admin).
 */
export async function clearMyRole(): Promise<void> {
  const deviceId = getDeviceId();
  if (!deviceId) return;

  const { error } = await supabase
    .from('visitors')
    .update({ role: null })
    .eq('device_id', deviceId);

  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[clearMyRole]', error.message);
  }
}

/**
 * Lista pozelor pending pentru moderare.
 * Returneaza PhotoRow-uri brute (frontend-ul le randeaza).
 */
export async function listPendingPhotos(): Promise<PhotoRow[]> {
  const { data, error } = await supabase
    .from('photos')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as PhotoRow[];
}

/**
 * Numarul de poze pending (pentru badge in AdminBar).
 */
export async function countPendingPhotos(): Promise<number> {
  const { count, error } = await supabase
    .from('photos')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'pending');

  if (error) throw error;
  return count ?? 0;
}

export async function approvePhoto(photoId: string, reviewerName?: string): Promise<void> {
  const { error } = await supabase
    .from('photos')
    .update({
      status: 'approved',
      reviewed_by: reviewerName ?? null,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', photoId);

  if (error) throw error;
}

export async function approvePhotos(photoIds: string[], reviewerName?: string): Promise<void> {
  if (photoIds.length === 0) return;
  const { error } = await supabase
    .from('photos')
    .update({
      status: 'approved',
      reviewed_by: reviewerName ?? null,
      reviewed_at: new Date().toISOString(),
    })
    .in('id', photoIds);

  if (error) throw error;
}

export async function rejectPhoto(
  photoId: string,
  reviewerName?: string,
  reason?: string,
): Promise<void> {
  const { error } = await supabase
    .from('photos')
    .update({
      status: 'rejected',
      reviewed_by: reviewerName ?? null,
      reviewed_at: new Date().toISOString(),
      reject_reason: reason ?? null,
    })
    .eq('id', photoId);

  if (error) throw error;
}

export async function rejectPhotos(
  photoIds: string[],
  reviewerName?: string,
  reason?: string,
): Promise<void> {
  if (photoIds.length === 0) return;
  const { error } = await supabase
    .from('photos')
    .update({
      status: 'rejected',
      reviewed_by: reviewerName ?? null,
      reviewed_at: new Date().toISOString(),
      reject_reason: reason ?? null,
    })
    .in('id', photoIds);

  if (error) throw error;
}

/**
 * Lista pozelor deja aprobate pentru vizualizare si stergere din galerie.
 */
export async function listApprovedPhotosAdmin(): Promise<PhotoRow[]> {
  const { data, error } = await supabase
    .from('photos')
    .select('*')
    .eq('status', 'approved')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as PhotoRow[];
}

/**
 * Sterge o poza definitiv din galerie (sau o marcheaza rejected).
 */
export async function deletePhoto(photoId: string): Promise<void> {
  const { error } = await supabase
    .from('photos')
    .delete()
    .eq('id', photoId);

  if (error) {
    const { error: e2 } = await supabase
      .from('photos')
      .update({ status: 'rejected' })
      .eq('id', photoId);
    if (e2) throw e2;
  }
}

// =============================================================
// User Moderation — lista de useri, ban/unban, statistici per user
// =============================================================

export interface UserRow {
  device_id: string;
  display_name: string | null;
  role: AdminRole | null;
  is_banned: boolean;
  suspended_at: string | null;
  suspended_reason: string | null;
  page_views: number;
  last_seen: string;
  first_seen: string;
  // Agregate
  photos_total: number;
  photos_approved: number;
  photos_pending: number;
  comments_count: number;
  likes_given: number;
}

/**
 * Lista toti userii (vizitatori) cu statistici agregate.
 * Folosita in /admin/users.
 */
export async function listUsers(): Promise<UserRow[]> {
  // Query 1: toti vizitatorii cu metadata
  const { data: visitors, error: e1 } = await supabase
    .from('visitors')
    .select('*')
    .order('last_seen', { ascending: false });

  // eslint-disable-next-line no-console
  console.log('[listUsers] visitors fetched:', { count: visitors?.length ?? 0, error: e1?.message });

  if (e1) throw e1;
  if (!visitors || visitors.length === 0) return [];

  // Query 2: agregat poze per uploader (uploaded_by = display_name, uploader_uuid = device_id)
  const { data: photoStats, error: e2 } = await supabase
    .from('photos')
    .select('uploaded_by, uploader_uuid, status');

  if (e2) throw e2;

  // Query 3: numar comentarii per author (display_name, fallback device_id)
  const { data: commentStats, error: e3 } = await supabase
    .from('comments')
    .select('author');

  if (e3) throw e3;

  // Query 4: likes per user_token (device_id)
  const { data: likeStats, error: e4 } = await supabase
    .from('likes')
    .select('user_token');

  if (e4) throw e4;

  // Indexare in memorie (lista useri e mica, max cateva sute).
  // Mapam per device_id (uploader_uuid) si per display_name (uploaded_by) ca sa fim siguri.
  const photoAgg = new Map<string, { total: number; approved: number; pending: number }>();
  for (const p of photoStats ?? []) {
    const keys = [p.uploader_uuid, p.uploaded_by].filter((k): k is string => !!k);
    for (const k of keys) {
      const cur = photoAgg.get(k) ?? { total: 0, approved: 0, pending: 0 };
      cur.total += 1;
      if (p.status === 'approved') cur.approved += 1;
      else if (p.status === 'pending') cur.pending += 1;
      photoAgg.set(k, cur);
    }
  }

  // Comentarii — mapam author la device_id prin visitors.display_name
  const visitorsByName = new Map<string, string>();
  for (const v of visitors) {
    if (v.display_name) visitorsByName.set(v.display_name, v.device_id);
  }
  const commentAgg = new Map<string, number>();
  for (const c of commentStats ?? []) {
    const did = visitorsByName.get(c.author) ?? c.author;
    commentAgg.set(did, (commentAgg.get(did) ?? 0) + 1);
  }

  const likeAgg = new Map<string, number>();
  for (const l of likeStats ?? []) {
    likeAgg.set(l.user_token, (likeAgg.get(l.user_token) ?? 0) + 1);
  }

  return (visitors as VisitorRow[]).map((v) => ({
    device_id: v.device_id,
    display_name: v.display_name ?? null,
    role: (v.role as AdminRole | null) ?? null,
    is_banned: v.is_banned ?? false,
    suspended_at: v.suspended_at ?? null,
    suspended_reason: v.suspended_reason ?? null,
    page_views: v.page_views ?? 0,
    last_seen: v.last_seen,
    first_seen: v.first_seen,
    photos_total: photoAgg.get(v.device_id)?.total ?? 0,
    photos_approved: photoAgg.get(v.device_id)?.approved ?? 0,
    photos_pending: photoAgg.get(v.device_id)?.pending ?? 0,
    comments_count: commentAgg.get(v.device_id) ?? 0,
    likes_given: likeAgg.get(v.device_id) ?? 0,
  }));
}

interface VisitorRow {
  device_id: string;
  display_name: string | null;
  role: string | null;
  is_banned: boolean;
  suspended_at: string | null;
  suspended_reason: string | null;
  page_views: number;
  last_seen: string;
  first_seen: string;
  user_agent: string | null;
}

/**
 * Toate pozele unui user (approved + pending + rejected), cu comentarii.
 * Caută atât pe uploaded_by (display_name) cat si pe uploader_uuid (device_id)
 * ca sa fim siguri ca prindem toate pozele.
 */
export async function getUserPhotos(deviceId: string): Promise<PhotoRow[]> {
  const { data, error } = await supabase
    .from('photos')
    .select('*')
    .or(`uploaded_by.eq.${deviceId},uploader_uuid.eq.${deviceId}`)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data ?? []) as PhotoRow[];
}

/**
 * Ban-uieste un user.
 *  - Pozele lui: setate status='rejected' (nu apar in galerie). Likes-urile si
 *    view-urile de PE pozele lui raman (count-ul se pastreaza), la fel si
 *    likes-urile si view-urile pe care LE-A DAT el la pozele altora.
 *  - Comentariile lui: sterse fizic.
 *  - User-ul: marcat is_banned=true → nu mai poate incarca/comenta.
 *  - Likes-urile date de el la alte poze: NU sunt atinse.
 *  - View-urile lui la alte poze: NU sunt atinse.
 */
export async function banUser(deviceId: string, reason?: string): Promise<{
  rejectedPhotos: number;
  deletedComments: number;
}> {
  // 1. Marcheaza userul ca banat
  const { error: banErr } = await supabase
    .from('visitors')
    .update({
      is_banned: true,
      suspended_at: new Date().toISOString(),
      suspended_reason: reason?.trim() || null,
    })
    .eq('device_id', deviceId);

  if (banErr) throw banErr;

  // 2. Respinge pozele userului. uploaded_by ramane setat (coloana e NOT NULL),
  //    dar status='rejected' => nu apar in galerie (RLS permite anon doar approved).
  //    Likes/views pe pozele astea raman intacte (FK nu CASCADE, le pastram).
  //    Cautam pe uploaded_by SAU uploader_uuid ca sa fim siguri.
  const { data: photos, error: photosErr } = await supabase
    .from('photos')
    .update({ status: 'rejected' })
    .or(`uploaded_by.eq.${deviceId},uploader_uuid.eq.${deviceId}`)
    .select('id');

  if (photosErr) throw photosErr;

  // 3. Stergem comentariile userului (prin device_id populat de frontend la insert).
  //    Likes-urile si view-urile lui pe pozele altora NU sunt atinse.
  const { data: comments, error: commentsErr } = await supabase
    .from('comments')
    .delete()
    .eq('device_id', deviceId)
    .select('id');

  if (commentsErr) throw commentsErr;

  return {
    rejectedPhotos: photos?.length ?? 0,
    deletedComments: comments?.length ?? 0,
  };
}

/**
 * Ridulza suspendarea unui user.
 */
export async function unbanUser(deviceId: string): Promise<void> {
  const { error } = await supabase
    .from('visitors')
    .update({
      is_banned: false,
      suspended_at: null,
      suspended_reason: null,
    })
    .eq('device_id', deviceId);

  if (error) throw error;
}

/**
 * Verifica daca user-ul curent e banat (citit din visitors).
 * Folosit de UI (Upload/Comment/Gallery) pentru a afisa mesajul de suspendare.
 */
export async function checkMyBanStatus(): Promise<{
  is_banned: boolean;
  reason: string | null;
}> {
  const deviceId = getDeviceId();
  if (!deviceId) return { is_banned: false, reason: null };

  const { data, error } = await supabase
    .from('visitors')
    .select('is_banned, suspended_reason')
    .eq('device_id', deviceId)
    .maybeSingle();

  if (error) {
    console.warn('[checkMyBanStatus]', error.message);
    return { is_banned: false, reason: null };
  }

  return {
    is_banned: data?.is_banned ?? false,
    reason: data?.suspended_reason ?? null,
  };
}
